import { useEffect, useRef, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { BsFiletypeCsv } from 'react-icons/bs';
import { FaFilePdf } from 'react-icons/fa6';
import { RiDeleteBinLine } from 'react-icons/ri';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Breadcrumbs from '../../components/Breadcrumbs';
import Button from '../../components/button';
import CustomModal from '../../components/CustomModal';
import Filters from '../../components/Filters';
import MultiLineText from '../../components/MultiLineText';
import NewTableComponent from '../../components/NewTableComponent';
import Notification from '../../components/Notification';
import { fetchNotificationsData } from '../../store/Slices/NotificationSlice';
import { deleteService, fetchServices, serviceExcelExport, servicePdfExport, updateServicePriorityApi, updateServiceStatusApi } from '../../store/Slices/ServiceSlice';
import '../../styles/Services.css';
import { priorityOptions, serviceStatusOptions } from '../../utils/Dropdowns';
import { hasPermission } from '../../utils/helper';
import { closedIcon, exportDataIcon, highPriorityIcon, inProgressIcon, lowPriorityIcon, mediumPriorityIcon, openIcon, pendingIcon, plusIcon, resolvedIcon } from '../../utils/svgs';
import { errorNotify, successNotify } from '../../utils/toast';
import ServiceBoxChart from './ServiceBoxChart';

const Services = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { getServicesPaginated, getServicesLoading, deleteLoading, servicePdfExportLoading, serviceExcelExportLoading } = useSelector((state) => state.services)

    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [searchText, setSearchText] = useState('');
    const [deleteUserPopup, setDeleteUserPopup] = useState(false);
    const [deleteUserId, setDeleteUserId] = useState(null);
    const [showExportOption, setShowExportOption] = useState(false);
    const [searchFilters, setSearchFilters] = useState(null);
    const [priorityPopup, setPriorityPopup] = useState({ open: false, rowId: null, anchorPos: { x: 0, y: 0 } });
    const [tableLoading, setTableLoading] = useState(false);
    const [openFilterName, setOpenFilterName] = useState(null);

    const [statusPopup, setStatusPopup] = useState({ open: false, rowId: null, anchorPos: { x: 0, y: 0 } });

    const canCreate = hasPermission('services', 'create');
    const canDelete = hasPermission('services', 'delete');

    const popupRef = useRef();
    const statusPopupRef = useRef();

    useEffect(() => {
        dispatch(fetchNotificationsData());
    }, [])

    useEffect(() => {
        function handleClickOutside(event) {
            if (statusPopupRef.current && !statusPopupRef.current.contains(event.target)) {
                setStatusPopup({ open: false, rowId: null, anchorPos: { x: 0, y: 0 } });
            }
        }
        if (statusPopup.open) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [statusPopup.open]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (popupRef.current && !popupRef.current.contains(event.target)) {
                setPriorityPopup({ open: false, rowId: null, anchorPos: { x: 0, y: 0 } });
            }
        }
        if (priorityPopup.open) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [priorityPopup.open]);

    useEffect(() => {
        if (searchText.length > 0 || searchFilters) {
            dispatch(fetchServices({
                page: currentPage,
                limit: limit,
                search_text: searchText,
                searchFilters: searchFilters
            }));
        }
        else {
            dispatch(fetchServices({
                page: currentPage,
                limit: limit,
            }));
        }
    }, [searchText, currentPage, limit, searchFilters]);

    const handlePriorityClick = (e, row) => {
        const rect = e.target.getBoundingClientRect();
        setPriorityPopup({
            open: true,
            rowId: row.id,
            anchorPos: { x: rect.right + 8, y: rect.top }
        });
    };

    const handlePrioritySelect = async (priority, row) => {
        setTableLoading(true);
        try {
            await dispatch(updateServicePriorityApi({ maintenances_service_id: row.id, priority })).unwrap();
            dispatch(fetchServices({
                page: currentPage,
                limit: limit,
                search_text: searchText,
                searchFilters: searchFilters
            }));
            successNotify('Priority updated successfully');
            setPriorityPopup({ open: false, rowId: null, anchorPos: { x: 0, y: 0 } });
        } catch (error) {
            errorNotify(error);
        } finally {
            // setTableLoading(false);
        }
    };

    const handleStatusSelect = async (status, row) => {
        setTableLoading(true);
        try {
            await dispatch(updateServiceStatusApi({ status, service_issue_id: row.id })).unwrap();
            dispatch(fetchServices({
                page: currentPage,
                limit: limit,
                search_text: searchText,
                searchFilters: searchFilters
            }));
            setStatusPopup({ open: false, rowId: null, anchorPos: { x: 0, y: 0 } });
            successNotify('Status updated successfully');
        } catch (err) {
            errorNotify(err);
        } finally {
            // setTableLoading(false);
        }
    };

    const commonColumns = {
        checked: true,
        disabled: true,
        hide: false,
        exportColumn: true,
        sortable: false,
    }

    const columns = [
        {
            ...commonColumns,
            name: "Service ID",
            key: "service_id",
            selector: (row) => (
                <span
                    className="hyperlink"
                    onClick={() => navigate(`/services/edit-service-entry/${row?.id}`)}
                    style={{ cursor: 'pointer' }}
                >
                    {row?.service_id}
                </span>
            ),
            // width: "140px",
            tableColumn: false,
        },
        {
            ...commonColumns,
            name: "Vehicle",
            key: "registration_number",
            selector: (row) => row?.vehicle?.registration_number,
            // width: "140px",
            tableColumn: true,
            filterName: "vehicleFilter",
            type: "text",
        },
        {
            ...commonColumns,
            name: "Completion Date",
            key: "complete_at",
            selector: (row) => "" + new Date(row?.complete_at).toLocaleDateString(),
            // width: "140px",
            tableColumn: true,
            filterName: "completedDateFilter",
            type: "date",
        },
        {
            ...commonColumns,
            name: "Priority",
            key: "priority",
            modifyData: (row) => row?.priority,
            tableColumn: true,
            filterName: "priorityFilter",
            type: "text",
            // width: "180px",
            selector: (row, index) => (
                <div style={{ display: "inline-block", overflow: "visible" }}>
                    <div
                        className={`priority ${row?.priority}`}
                        style={{ cursor: "pointer" }}
                        onClick={e => handlePriorityClick(e, row)}
                    >
                        <div className='d-flex align-items-center gap-1'>
                            <span>
                                {row?.priority === 'low' ? lowPriorityIcon : row?.priority === 'medium' ? mediumPriorityIcon : row?.priority === 'high' ? highPriorityIcon : null}
                            </span>
                            {row?.priority}
                        </div>

                        {
                            priorityPopup.open && priorityPopup.rowId === row.id &&
                            <PriorityPopup row={row} onSelect={handlePrioritySelect} index={index} />
                        }
                    </div>
                </div>
            ),
        },
        {
            ...commonColumns,
            name: "Meter Reading",
            key: "odo_meter",
            selector: (row) => row?.odo_meter,
            // width: "140px",
            tableColumn: true,
            filterName: "odoMeterFilter",
            type: "number",
        },
        {
            ...commonColumns,
            name: "Service Tasks Performed",
            key: "service_line_items",
            tableColumn: true,
            filterName: "serviceLinePartFilter",
            type: "text",
            selector: (row) => {
                const serviceItems = row?.service_line_items || [];

                if (serviceItems.length === 0) {
                    return <span className="no-items">No tasks</span>;
                }

                if (serviceItems.length === 1) {
                    return (
                        <span className="service-task">
                            <MultiLineText text={serviceItems[0]?.part?.description || serviceItems[0]?.part?.description} maxWidth={"90px"} />
                        </span>
                    );
                }

                const firstItem = serviceItems[0]?.part?.description || serviceItems[0]?.part?.description;
                const remainingCount = serviceItems.length - 1;

                return (
                    <div className="service-tasks-container">
                        <span className="first-task">
                            <MultiLineText text={firstItem} maxWidth={"90px"} />
                        </span>

                        <OverlayTrigger
                            placement="top"
                            overlay={
                                <Tooltip>
                                    <div>
                                        {serviceItems.slice(1).map((item, idx) => (
                                            <div key={idx} className='tooltip_item'>{item?.part?.description}</div>
                                        ))}
                                    </div>
                                </Tooltip>
                            }
                        >
                            <span className="additional-tasks">+{remainingCount}</span>
                        </OverlayTrigger>

                        {/*                                     
                        <Tooltip content={serviceItems} position={isLastFour ? "top" : "bottom"}>
                            <span className="additional-tasks"> +{remainingCount}</span>
                        </Tooltip> */}
                    </div>
                );
            },
            width: "200px",
        },
        {
            ...commonColumns,
            name: "Status",
            key: "status",
            selector: (row, index) => (
                <div style={{ display: "inline-block", overflow: "visible" }}>
                    <div
                        className={`status ${row?.status}`}
                        style={{ cursor: "pointer", textTransform: "capitalize" }}
                        onClick={e => handleStatusClick(e, row)}
                    >
                        <div className='d-flex align-items-center gap-1'>
                            {row?.status === "pending" ? pendingIcon : row?.status === "completed" ? resolvedIcon : row?.status === "in_progress" ? inProgressIcon : row?.status === "in_progress" ? inProgressIcon : null}
                            {row?.status === "in_progress" ? "In Progress" : row?.status}
                        </div>

                        {statusPopup.open && statusPopup.rowId === row.id &&
                            <StatusPopup row={row} onSelect={handleStatusSelect} index={index} />
                        }
                    </div>
                </div>
            ),
            tableColumn: true,
            filterName: "statusFilter",
            type: "text",
            width: "120px",
        },
        {
            ...commonColumns,
            name: "Total Cost (PKR)",
            key: "doj",
            selector: (row) => row?.complete_total,
            modifyData: (row) => row?.complete_total,
            tableColumn: true,
            filterName: "totalMeterFilter",
            type: "number",
        },
        (canDelete) && {
            name: "Actions",
            key: "actions",
            checked: true,
            disabled: false,
            hide: false,
            exportColumn: false,
            selector: (row) => (
                <div className="table_action_btn">
                    {canDelete && (
                        <button onClick={() => {
                            setDeleteUserPopup(true);
                            setDeleteUserId(row?.id);
                        }} className="delete_btn">
                            <RiDeleteBinLine />
                        </button>
                    )}
                </div>
            ),
            sortable: false,
            tableColumn: false
        }
    ].filter(Boolean);

    const handleStatusClick = (e, row) => {
        const rect = e.target.getBoundingClientRect();
        setStatusPopup({
            open: true,
            rowId: row.id,
            anchorPos: { x: rect.right + 8, y: rect.top }
        });
    };

    const StatusPopup = ({ row, onSelect, index }) => {

        const windowHeight = document.documentElement.clientHeight;
        const popupHeight = 120;

        const availableSpaceAbove = index * popupHeight;
        const availableSpaceBelow = windowHeight - (availableSpaceAbove + popupHeight);

        let positionStyle = {};
        if (availableSpaceBelow > availableSpaceAbove) {
            positionStyle = { top: "25px" };
        } else {
            positionStyle = { bottom: "25px" };
        }

        return (
            <div
                ref={statusPopupRef}
                style={{
                    position: "absolute",
                    left: "0px",
                    background: "#FAF9FB",
                    border: "1px solid #D1D2E0",
                    borderRadius: "8px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    zIndex: 4,
                    minWidth: "100px",
                    ...positionStyle
                }}>

                {false && (
                    <div
                        style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            background: "rgba(255,255,255,0.6)",
                            zIndex: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "not-allowed"
                        }}
                    >
                        <span style={{ color: "#4014B8", fontWeight: 600 }}>Updating...</span>
                    </div>
                )}

                <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                    {serviceStatusOptions.map((status) => (
                        <div key={status.value}
                            style={{ display: "flex", alignItems: "center", padding: "5px 5px", gap: "10px", justifyContent: "center", backgroundColor: row.status === status?.value ? 'rgb(156 159 201 / 15%)' : "transparent" }}>
                            <div
                                className={`status ${status.value}`}
                                onClick={() => onSelect(status.value, row)}
                                style={{ cursor: "pointer", textTransform: "capitalize" }}>
                                <div className='d-flex align-items-center gap-1'>
                                    {status.value === "pending" ? pendingIcon : status.value === "open" ? openIcon : status.value === "closed" ? closedIcon : status.value === "approved" ? resolvedIcon : status.value === "resolved" ? resolvedIcon : status.value === "completed" ? resolvedIcon : status.value === "in_progress" ? inProgressIcon : null}
                                    {status.label}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        )
    }

    const handleDeleteUser = async () => {
        try {
            await dispatch(deleteService(deleteUserId)).unwrap();
            successNotify('Service deleted successfully');
            setDeleteUserPopup(false);
            setDeleteUserId(null);

            dispatch(fetchServices({
                page: currentPage,
                limit: limit,
                searchFilters: searchFilters
            }));
        } catch (error) {
            errorNotify(error);
        }
    };

    const handleLimitChange = (newLimit) => {
        setCurrentPage(1);
        setLimit(newLimit);
    };

    const handleSearch = (text) => {
        setSearchText(text);
        setCurrentPage(1);
    };

    const exportPdfHandler = () => {
        const results = getServicesPaginated?.results
        const getIds = []
        results.forEach(item => {
            getIds.push(item.id)
        })

        const data = {
            service_ids: getIds
        }

        dispatch(servicePdfExport(data)).unwrap()
            .then((response) => {
                const fileUrl = `${process.env.REACT_APP_API_FILES_URL}${response.data}`;
                const link = document.createElement("a");
                link.href = fileUrl;
                link.target = "_blank";
                link.setAttribute("download", `service.pdf`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            })
    }

    const exportExcelHandler = () => {
        const results = getServicesPaginated?.results
        const getIds = []
        results.forEach(item => {
            getIds.push(item.id)
        })

        const data = {
            service_ids: getIds
        }

        dispatch(serviceExcelExport(data)).unwrap()
            .then((response) => {
                const fileUrl = `${process.env.REACT_APP_API_FILES_URL}${response.data}`;
                const link = document.createElement("a");
                link.href = fileUrl;
                link.target = "_blank";
                link.setAttribute("download", `service.xlsx`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            })
    }

    const processedResults = (getServicesPaginated?.results || []).map((item, idx, arr) => {
        const isLastFour = idx >= arr.length - 4;
        return { ...item, isLastFourServiceRow: isLastFour };
    });

    const PriorityPopup = ({ row, onSelect, index }) => {

        const windowHeight = document.documentElement.clientHeight;
        const popupHeight = 120;
        const availableSpaceAbove = index * popupHeight;
        const availableSpaceBelow = windowHeight - (availableSpaceAbove + popupHeight);

        let positionStyle = {};

        if (availableSpaceBelow > availableSpaceAbove) {
            positionStyle = { top: "30px" };
        } else {
            positionStyle = { bottom: "30px" };
        }

        return (
            <div
                ref={popupRef}
                style={{
                    position: "absolute",
                    left: "0px",
                    background: "#FAF9FB",
                    border: "1px solid #D1D2E0",
                    borderRadius: "8px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    zIndex: 4,
                    minWidth: "100px",
                    ...positionStyle
                }}
            >
                <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                    {priorityOptions.map((priority) => (
                        <div key={priority.value} onClick={() => onSelect(priority?.value, row)}
                            style={{ display: "flex", alignItems: "center", padding: "5px 5px", gap: "10px", justifyContent: "center", backgroundColor: row.priority === priority?.value ? 'rgb(156 159 201 / 15%)' : "transparent" }}>
                            <div className={`priority ${priority?.value}`}>
                                <div className='d-flex align-items-center gap-1'>
                                    {priority?.value === 'low' ? lowPriorityIcon : priority?.value === 'medium' ? mediumPriorityIcon : priority?.value === 'high' ? highPriorityIcon : null}
                                    {priority.label}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    useEffect(() => {
        function handleClickOutside(event) {
            // Only close if click is outside the export button and dropdown
            if (!event.target.closest('.export_options') && !event.target.closest('.services_buttons button')) {
                setShowExportOption(false);
            }
        }
        if (showExportOption) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showExportOption]);

    return (
        <div>
            <Breadcrumbs values={['Maintenance', 'Overview']} />

            <CustomModal show={deleteUserPopup} handleClose={() => setDeleteUserPopup(false)} size="md" heading="Delete Service" customHeaderCss="custom_header_css">
                <div className="delete_user_modal_body">
                    <p>Are you sure you want to delete this Service?</p>

                    <Button
                        cancelBtn={() => setDeleteUserPopup(false)}
                        submitBtn={handleDeleteUser}
                        loading={deleteLoading}
                        disabled={deleteLoading}
                        buttonExtraCss="delete_user_modal_btn"
                        submitText="Delete"
                        type="submit"
                    />
                </div>
            </CustomModal>

            <div className='access_view'>
                <h2>Overview</h2>

                <div className='services_buttons'>
                    <Notification />

                    <div style={{ position: "relative" }}>
                        <button onClick={() => setShowExportOption(!showExportOption)}>{exportDataIcon} Export Data</button>
                        {showExportOption && (
                            <div className='export_options'>
                                <span style={{ cursor: serviceExcelExportLoading ? "not-allowed" : "pointer" }} onClick={serviceExcelExportLoading ? null : exportExcelHandler}><BsFiletypeCsv style={{ fontSize: "19px", color: "green", cursor: serviceExcelExportLoading ? "not-allowed" : "pointer" }} /> As CSV</span>
                                <span style={{ cursor: servicePdfExportLoading ? "not-allowed" : "pointer" }} onClick={servicePdfExportLoading ? null : exportPdfHandler}><FaFilePdf style={{ fontSize: "19px", color: "red" }} /> As PDF</span>
                            </div>
                        )}
                    </div>
                    {canCreate && (
                        <button onClick={() => navigate('/services/service-entry')}>{plusIcon} New Service Record</button>
                    )}
                </div>
            </div>

            <ServiceBoxChart />

            {openFilterName && (
                <Filters
                    closeFilter={() => setOpenFilterName(null)}
                    searchFilters={searchFilters}
                    setSearchFilters={setSearchFilters}
                    filterName={openFilterName}
                    type={columns.find(col => col.filterName === openFilterName)?.type || "text"}
                />
            )}

            <NewTableComponent
                showAddBtn={false}
                columns={columns}
                data={processedResults}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                limit={limit}
                setLimit={handleLimitChange}
                totalItems={getServicesPaginated?.meta?.totalItems}
                totalPages={getServicesPaginated?.meta?.totalPages}
                onSearch={handleSearch}
                loading={tableLoading ? false : getServicesLoading}
                paginationLoading={tableLoading ? false : getServicesLoading}
                setSearchFilters={setSearchFilters}
                searchFilters={searchFilters}
            />
        </div>
    )
}

export default Services